import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getSupabaseConfigurationError, supabase } from "@/lib/supabase";

const AuthContext = createContext(null);

function normalizeEmail(value) {
  const match = String(value || "").trim().match(
    /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i
  );
  return (match?.[0] || "").toLowerCase();
}

const ADMIN_EMAIL =
  normalizeEmail(import.meta.env.VITE_ADMIN_EMAIL) || "admin@mangalferafilms.com";

const isAdminSession = (session) =>
  Boolean(
    session?.user?.email
    && normalizeEmail(session.user.email) === ADMIN_EMAIL
  );

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [authInitializing, setAuthInitializing] = useState(true);
  const [authError, setAuthError] = useState("");

  const user = session?.user || null;
  const isAdmin = useMemo(() => isAdminSession(session), [session]);

  const refresh = useCallback(async () => {
    if (!supabase) {
      setSession(null);
      setAuthInitializing(false);
      return null;
    }

    setAuthInitializing(true);
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;

      const nextSession = data.session || null;
      if (nextSession?.user && !isAdminSession(nextSession)) {
        setAuthError("Unauthorized admin email");
        setSession(null);
        await supabase.auth.signOut();
        return null;
      }

      setAuthError("");
      setSession(nextSession);
      return nextSession;
    } finally {
      setAuthInitializing(false);
    }
  }, []);

  useEffect(() => {
    let active = true;

    refresh().catch((error) => {
      console.error("Supabase session check failed", error);
      if (active) {
        setSession(null);
        setAuthInitializing(false);
      }
    });

    if (!supabase) return () => { active = false; };

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        if (!active) return;

        if (nextSession?.user && !isAdminSession(nextSession)) {
          setAuthError("Unauthorized admin email");
          setSession(null);
          setTimeout(() => {
            supabase.auth.signOut().catch((error) => {
              console.error("Unauthorized Supabase sign-out failed", error);
            });
          }, 0);
        } else {
          setAuthError("");
          setSession(nextSession || null);
        }

        setAuthInitializing(false);
      }
    );

    return () => {
      active = false;
      authListener.subscription.unsubscribe();
    };
  }, [refresh]);

  const login = async (email, password) => {
    if (!supabase) {
      throw new Error(getSupabaseConfigurationError() || "Supabase is not configured");
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (error) throw error;

    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) throw sessionError;

    const nextSession = sessionData.session || data.session || null;
    if (!isAdminSession(nextSession)) {
      await supabase.auth.signOut();
      setSession(null);
      setAuthError("Unauthorized admin email");
      throw new Error("Unauthorized admin email");
    }

    setAuthError("");
    setSession(nextSession);
    setAuthInitializing(false);
    return nextSession.user;
  };

  const logout = async () => {
    if (supabase) {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    }
    setSession(null);
    setAuthError("");
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        isAdmin,
        loading: authInitializing,
        authInitializing,
        authError,
        login,
        logout,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
